import React from "react";
import LegalLayout, { Section, P, UL, LI, Callout } from "./LegalLayout";

export default function CancellationPolicy() {
  return (
    <LegalLayout
      code="DOC · 04 / CANCELLATION POLICY"
      title="Cancellation Policy"
      lastUpdated="December 2025"
    >
      <Section id="overview" title="1. Overview">
        <P>
          This Cancellation Policy explains the procedure and timelines for
          cancelling an enrollment, mentorship engagement, or fractional
          contract with Sebyx. Refund eligibility for each cancellation is
          governed by our separate{" "}
          <a
            href="/refund-policy"
            className="text-sebyx-accent hover:underline"
          >
            Refund Policy
          </a>
          .
        </P>
      </Section>

      <Section id="customer-initiated" title="2. Customer-Initiated Cancellation">
        <P>
          You may cancel any Sebyx Academy enrollment by sending a written
          request to{" "}
          <a
            href="mailto:contact@sebyx.com"
            className="text-sebyx-accent hover:underline"
          >
            contact@sebyx.com
          </a>{" "}
          with the subject line "Cancellation Request — [Tier Name]". Please
          include:
        </P>
        <UL>
          <LI>The name and email used at purchase</LI>
          <LI>Razorpay payment ID (begins with <code>pay_…</code>)</LI>
          <LI>A brief reason for cancellation (optional but appreciated)</LI>
        </UL>
        <P>
          Cancellation is treated as effective from the date Sebyx acknowledges
          receipt of your request. Refund (if any) is processed per the Refund
          Policy applicable to your tier.
        </P>
      </Section>

      <Section id="cohort-cancellation" title="3. Cohort Cancellation Windows (Pro Tier)">
        <UL>
          <LI>
            <strong>≥ 7 days before cohort start:</strong> Cancellation
            accepted; full refund or free deferral to the next available
            cohort.
          </LI>
          <LI>
            <strong>24 hours – 7 days before cohort start:</strong>{" "}
            Cancellation accepted; partial refund per Refund Policy.
          </LI>
          <LI>
            <strong>After cohort start:</strong> Enrollment cannot be
            cancelled. Participants may opt out at any time, but no refund is
            issued.
          </LI>
        </UL>
      </Section>

      <Section id="elite-cancellation" title="4. Elite Mentorship Cancellation">
        <UL>
          <LI>
            <strong>Before kickoff session:</strong> Cancellation accepted;
            full refund.
          </LI>
          <LI>
            <strong>Within first 7 days of kickoff:</strong> Cancellation
            accepted; pro-rata refund for unused weeks.
          </LI>
          <LI>
            <strong>After Day 7:</strong> Cancellation accepted, but no refund
            issued. Remaining scheduled sessions can be utilized within the
            engagement window.
          </LI>
        </UL>
      </Section>

      <Section id="rescheduling" title="5. Rescheduling vs. Cancellation">
        <Callout>
          A reschedule is <strong>not</strong> a cancellation. You may
          reschedule a Pro cohort enrollment to the next batch (subject to seat
          availability) without penalty if requested at least 7 days before
          your original cohort begins. Elite mentorship sessions can be
          rescheduled with 24 hours' prior notice.
        </Callout>
      </Section>

      <Section id="sebyx-initiated" title="6. Sebyx-Initiated Cancellation">
        <P>
          Sebyx reserves the right to cancel, postpone, or restructure any
          cohort, mentorship engagement, or fractional contract in the
          following circumstances:
        </P>
        <UL>
          <LI>
            Insufficient enrollment to run a cohort effectively (minimum
            threshold not met).
          </LI>
          <LI>Faculty unavailability due to medical or force majeure events.</LI>
          <LI>
            Material breach of these Terms or our{" "}
            <a
              href="/terms-of-service"
              className="text-sebyx-accent hover:underline"
            >
              Terms of Service
            </a>{" "}
            by the participant — including disrespectful conduct, IP
            infringement, or unauthorized recording.
          </LI>
          <LI>
            Suspected fraud, chargeback, or non-genuine intent established
            after enrollment.
          </LI>
        </UL>
        <P>
          In Sebyx-initiated cancellations made for reasons other than
          participant breach, you will receive a full refund or a no-cost
          transfer to the next available cohort, at your choice.
        </P>
      </Section>

      <Section id="bdaas" title="7. BDaaS & Fractional Contracts">
        <P>
          Fractional Leadership and BDaaS engagements are governed by the
          specific Statement of Work signed with the client. Cancellation
          notice periods, kill-fees, and exit terms are defined in that SOW
          and supersede this general policy.
        </P>
      </Section>

      <Section id="effect" title="8. Effect of Cancellation">
        <UL>
          <LI>
            Access to the relevant program, recordings, and digital assets is
            revoked from the cancellation effective date.
          </LI>
          <LI>
            Outstanding deliverables and confidentiality obligations on both
            sides survive cancellation.
          </LI>
          <LI>
            Refunds (where due) are processed via Razorpay back to the
            original payment instrument within 5–10 working days.
          </LI>
        </UL>
      </Section>

      <Section id="contact" title="9. Contact">
        <P>
          For cancellation requests or clarifications, write to{" "}
          <a
            href="mailto:contact@sebyx.com"
            className="text-sebyx-accent hover:underline"
          >
            contact@sebyx.com
          </a>{" "}
          or call{" "}
          <a
            href="tel:+919108436667"
            className="text-sebyx-accent hover:underline"
          >
            +91 91084 36667
          </a>
          .
        </P>
      </Section>
    </LegalLayout>
  );
}
